import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { trackEvent } from "@lib/infrastructure";
import { getFloorById, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { Icon } from "@atoms";
import { IconButton } from "@molecules";
import { cn } from "@utils";

import { HaNContext } from "../shell/HaNContext";
import { useChangeFloor } from "./useChangeFloor";

type FloorSuggestionTileProps = {
  floorId: string;
  direction: "above" | "below";
  // A line under the floor's name, for tabs without usage stats
  description?: string;
  initiallyExpanded?: boolean;
  children?: ReactNode;
};

/**
 * A floor next to the selected one, with how much is available there. Clicking it moves the page to that floor. In
 * the list it holds the floor's suggestions, which can be collapsed; in the carousel it stands on its own.
 */
export const FloorSuggestionTile = ({
  floorId,
  direction,
  description,
  initiallyExpanded = true,
  children,
}: FloorSuggestionTileProps) => {
  const { t } = useTranslation();
  const { tab, building, usageStatsPerFloor } = useRequiredContext(HaNContext);
  const floor = useAppSelector(state => getFloorById(state, floorId));
  const changeFloor = useChangeFloor();
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  if (!floor) return null;

  const usageStats = usageStatsPerFloor.get(floorId);
  const trackingProps = { buildingId: building.buildingId ?? "", tab, floorId, type: direction };

  const moveToFloor = () => {
    changeFloor(floorId, () => {
      trackEvent("HereAndNow_OtherFloorSuggestions__FloorClick", trackingProps);
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);

    trackEvent(
      isExpanded ? "HereAndNow_OtherFloorSuggestions__Collapse" : "HereAndNow_OtherFloorSuggestions__Expand",
      trackingProps,
    );
  };

  return (
    <>
      <div className="flex w-full items-center gap-4 rounded-lg p-2 hover:bg-mapiq-black-50">
        <button
          type="button"
          className="group flex flex-grow items-center gap-3 text-left"
          onClick={moveToFloor}>
          <span className="flex size-12 flex-none items-center justify-center rounded-lg bg-beige-500* text-black">
            <Icon
              icon={direction === "above" ? "arrowUp" : "arrowDown"}
              className={cn("size-4 transition-transform", {
                "group-hover:-translate-y-1": direction === "above",
                "group-hover:translate-y-1": direction === "below",
              })}
            />
            <Icon
              icon="floor"
              className="size-4"
            />
          </span>

          <span className="flex flex-grow flex-col items-start">
            <span className="font-semibold">{`${t("Floor")} ${floor.number}`}</span>
            {description && <span className="text-mapiq-black-500">{description}</span>}

            {usageStats && (
              <>
                <span className="text-mapiq-black-500">
                  {tab === "rooms"
                    ? t("hereAndNow:AvailableRoom", { count: usageStats.available })
                    : t("hereAndNow:AvailableWorkspace", { count: usageStats.available })}
                </span>
                <span className="flex w-full max-w-40">
                  <span
                    className="block h-2 origin-left rounded-sm bg-functional-green transition-[width]"
                    style={{ width: `${usageStats.ratio * 100}%` }}
                  />
                </span>
              </>
            )}
          </span>
        </button>

        {children && (
          <IconButton
            aria-label={isExpanded ? t("ShowLess") : t("ShowAll")}
            aria-expanded={isExpanded}
            icon="caretDown"
            size="small"
            iconButton="tertiary"
            className={cn("[&>svg]:transition-transform", { "[&>svg]:rotate-180": isExpanded })}
            onClick={toggleExpanded}
          />
        )}
      </div>

      {isExpanded && children}
    </>
  );
};
